import { Box, Group, Paper, Stack, Text, Title, Button, Card, Loader, Textarea } from '@mantine/core';
import { IconCircleCheck, IconClipboard, IconCloudUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useOutletContext } from 'react-router';

import { PageWide } from '../../components/PageLayout';
import type { Game } from '../../data/games';
import { requestFiles, uploadMedia } from '../../data/media';
import { MediaTable } from '../../platforms/line/components/MediaTable';

export const CreativesTab = () => {
    const { gameId } = useOutletContext<{ game: Game; gameId: string }>();
    const [mediaCSV, setMediaCSV] = useState<string>();
    const [loading, setLoading] = useState(false);

    return (
        <PageWide>
            <Stack gap="lg">
                <Group align="center" gap="md">
                    <Box flex={1}>
                        <Text c="dimmed">
                            Select multiple image or video files to upload as creatives for your campaign. Supported
                            formats: JPG, PNG, GIF, MP4, MOV
                        </Text>
                    </Box>
                    <Button
                        onClick={async () => {
                            const files = await requestFiles();
                            if (files) {
                                setLoading(true);
                                const result = await uploadMedia({ gameId, files });
                                setMediaCSV(result.csv);
                                setLoading(false);
                            }
                        }}
                        disabled={loading}
                        leftSection={loading ? <Loader size={16} /> : <IconCloudUpload size={20} />}
                        style={{ flexShrink: 0 }}
                    >
                        {loading ? 'Uploading...' : 'Select Files'}
                    </Button>
                </Group>

                {mediaCSV && (
                    <Card withBorder>
                        <Card.Section inheritPadding py="sm">
                            <Title order={4}>Generated CSV Output</Title>
                        </Card.Section>
                        <Card.Section inheritPadding py="md">
                            <Stack gap="md">
                                <Group justify="space-between">
                                    <Group gap="xs">
                                        <Paper
                                            w={32}
                                            h={32}
                                            bg="light-dark(var(--mantine-color-green-1), var(--mantine-color-green-9))"
                                            c="light-dark(var(--mantine-color-teal-6), var(--mantine-color-teal-4))"
                                            radius="sm"
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <IconCircleCheck size={16} />
                                        </Paper>
                                        <Box>
                                            <Text fw={600}>Upload Complete</Text>
                                            <Text size="xs" c="dimmed">
                                                CSV data ready for use
                                            </Text>
                                        </Box>
                                    </Group>
                                    <Button
                                        onClick={() => {
                                            void navigator.clipboard.writeText(mediaCSV);
                                        }}
                                        variant="outline"
                                        leftSection={<IconClipboard size={16} />}
                                    >
                                        Copy CSV
                                    </Button>
                                </Group>
                                <Paper
                                    bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))"
                                    radius="sm"
                                    p="md"
                                >
                                    <Textarea
                                        rows={16}
                                        value={mediaCSV}
                                        readOnly
                                        styles={{ input: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
                                        placeholder="CSV content will appear here..."
                                    />
                                </Paper>
                            </Stack>
                        </Card.Section>
                    </Card>
                )}

                <Card withBorder>
                    <Card.Section inheritPadding py="sm">
                        <Title order={4}>LINE Media Library</Title>
                    </Card.Section>
                    <Card.Section inheritPadding py="md">
                        <MediaTable gameId={gameId} />
                    </Card.Section>
                </Card>
            </Stack>
        </PageWide>
    );
};
